import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View, type ViewInstance } from 'react-native';

import { Portal } from './portal';
import { Text } from './text';

export interface ActionMenuItem {
	label: string;
	icon?: keyof typeof Ionicons.glyphMap;
	onPress: () => void;
	disabled?: boolean;
	/** Render in red — for destructive actions. */
	destructive?: boolean;
	testID?: string;
	/** A colour dot shown in place of the icon (e.g. a property manager's chart colour). */
	swatch?: string;
	/** Marks the current choice with a check — for menus used as a dropdown select. */
	selected?: boolean;
}

export interface ActionMenuProps {
	actions: ActionMenuItem[];
	/** Trigger label. Defaults to "Actions". */
	label?: string;
	icon?: keyof typeof Ionicons.glyphMap;
	testID?: string;
	/** Compact, neutral trigger that matches a filter chip. */
	compact?: boolean;
	/** Which trigger edge the panel hangs from. Defaults to `right` (opens leftward). */
	align?: 'left' | 'right';
}

interface AnchorRect {
	top: number;
	right: number;
	left: number;
	width: number;
}

/**
 * A single trigger button that opens a dropdown of actions. Use when a toolbar
 * has more than ~2 actions, or needs to stay usable at narrow widths — the
 * trigger is fixed-width so it never wraps into an unreadable pile of buttons.
 *
 * The panel renders through a `Portal` at `position: fixed` (web) so it escapes
 * every ancestor stacking/scroll context, anchored to the trigger's bottom-right
 * (opens leftward to stay on-screen on small viewports). Closes on item select
 * or a tap on the full-screen backdrop.
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({ actions, label = 'Actions', icon = 'ellipsis-horizontal', testID, compact, align = 'right' }) => {
	const [open, setOpen] = useState(false);
	const [anchor, setAnchor] = useState<AnchorRect | null>(null);
	const triggerRef = useRef<ViewInstance>(null);

	const measure = useCallback(() => {
		const node = triggerRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			const vw = typeof window !== 'undefined' ? window.innerWidth : r.right;
			setAnchor({ top: r.bottom + 4, right: vw - r.right, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchor({ top: y + h + 4, right: 0, left: x, width: w }));
		}
	}, []);

	// Re-measure while open so the panel tracks scroll / resize.
	useEffect(() => {
		if (!open || Platform.OS !== 'web' || typeof window === 'undefined') return;
		const onMove = () => measure();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measure]);

	const toggle = () => {
		measure();
		setOpen((v) => !v);
	};

	const handlePick = (item: ActionMenuItem) => {
		if (item.disabled) return;
		setOpen(false);
		item.onPress();
	};

	const panelPosition = Platform.OS === 'web' && anchor
		? align === 'left'
			? {
				position: 'fixed' as 'absolute',
				top: anchor.top,
				// Clamp so a panel anchored near the right edge (a wrapped toolbar) stays on-screen.
				left: Math.max(8, Math.min(anchor.left, (typeof window !== 'undefined' ? window.innerWidth : anchor.left + 288) - 288)),
			}
			: { position: 'fixed' as 'absolute', top: anchor.top, right: anchor.right }
		: anchor
			? { position: 'absolute' as const, top: anchor.top, left: anchor.left }
			: { display: 'none' as const };

	return (
		<>
			<Pressable
				ref={triggerRef}
				onPress={toggle}
				style={compact ? { ...styles.trigger, ...styles.triggerCompact } : styles.trigger}
				testID={testID}
				accessibilityRole="button"
				accessibilityLabel={label}
			>
				<Ionicons name={icon} size={compact ? 14 : 18} color={compact ? '#546e7a' : '#1976d2'} />
				<Text style={compact ? { ...styles.triggerLabel, ...styles.triggerLabelCompact } : styles.triggerLabel}>{label}</Text>
				<Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={compact ? 14 : 16} color={compact ? '#546e7a' : '#1976d2'} />
			</Pressable>
			{open && (
				<Portal>
					<Pressable style={styles.backdrop} onPress={() => setOpen(false)} />
					<View style={[styles.panel, panelPosition]}>
						<ScrollView style={styles.panelScroll}>
							{actions.map((item, i) => (
								<Pressable
									key={`${item.label}-${i}`}
									onPress={() => handlePick(item)}
									disabled={item.disabled}
									testID={item.testID}
									style={({ pressed }) => [
										styles.item,
										pressed && !item.disabled && styles.itemPressed,
										item.disabled && styles.itemDisabled,
									]}
								>
									{item.swatch ? <View style={{ ...styles.swatch, backgroundColor: item.swatch }} /> : null}
									{!item.swatch && item.icon && (
										<Ionicons
											name={item.icon}
											size={18}
											color={item.destructive ? '#d32f2f' : '#1976d2'}
											style={styles.itemIcon}
										/>
									)}
									<Text style={[styles.itemLabel, item.destructive && styles.itemLabelDestructive]}>
										{item.label}
									</Text>
									{item.selected && <Ionicons name="checkmark" size={16} color="#1976d2" style={styles.itemCheck} />}
								</Pressable>
							))}
						</ScrollView>
					</View>
				</Portal>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	trigger: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		paddingVertical: 8,
		paddingHorizontal: 14,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#bbdefb',
		backgroundColor: '#e3f2fd',
	},
	triggerCompact: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 16, borderColor: '#cfd8dc', backgroundColor: '#fff' },
	triggerLabelCompact: { color: '#546e7a', fontSize: 13, fontWeight: '500' },
	panelScroll: { maxHeight: 320 },
	swatch: { width: 12, height: 12, borderRadius: 6, marginHorizontal: 4 },
	itemCheck: { marginLeft: 'auto' },
	triggerLabel: { color: '#1976d2', fontWeight: '600', fontSize: 14 },
	backdrop: {
		position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute',
		top: 0, left: 0, right: 0, bottom: 0,
	},
	panel: {
		minWidth: 220,
		maxWidth: 280,
		backgroundColor: '#ffffff',
		borderRadius: 10,
		paddingVertical: 6,
		borderWidth: 1,
		borderColor: '#e0e0e0',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.15,
		shadowRadius: 12,
		elevation: 8,
	},
	item: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 16, gap: 12 },
	itemPressed: { backgroundColor: '#f5f5f5' },
	itemDisabled: { opacity: 0.45 },
	itemIcon: { width: 20, textAlign: 'center' },
	itemLabel: { fontSize: 14, color: '#212121' },
	itemLabelDestructive: { color: '#d32f2f' },
});
